import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { resolveDocumentV3, type DocumentV3 } from '@docgen/schema';
import { SelectInput } from '@bcecloud/portal-ui';
import { DocumentView } from '../document/DocumentView';

/** A letter page at 96dpi: the template's `.page{width:8.5in}`. */
const PAGE_WIDTH_PX = 816;
/** The gap between the page and the screen edge, each side. */
const SIDE_GUTTER_PX = 12;
/** The frame width assumed before the first measurement: a small (360px) phone. */
const FALLBACK_FRAME_WIDTH_PX = 360;

export interface PhonePreviewProps {
  readonly doc: DocumentV3;
}

/** One entry in the page jump list. */
export interface PageJump {
  readonly key: string;
  readonly label: string;
}

/**
 * The jump list's entries: one per rendered page, numbered in order, named
 * after its template page (a repeated page, e.g. one per program, is numbered
 * within its run).
 */
export function pageJumps(doc: DocumentV3): readonly PageJump[] {
  const names = new Map(doc.pages.map((page) => [page.id, page.name]));
  const seen = new Map<string, number>();
  return resolveDocumentV3(doc).pages.map((page, index) => {
    const run = (seen.get(page.pageId) ?? 0) + 1;
    seen.set(page.pageId, run);
    const name = names.get(page.pageId) ?? 'Page';
    const repeated = page.key !== page.pageId;
    return { key: page.key, label: `${index + 1}. ${repeated ? `${name} ${run}` : name}` };
  });
}

/**
 * The proposal as the client will see it, read-only, each page scaled down to
 * the screen width so it reads top to bottom with no sideways scroll. Pinch
 * zoom stays with the browser. A jump list scrolls straight to any page.
 */
export function PhonePreview({ doc }: PhonePreviewProps): React.ReactElement {
  const frameRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const [pagesHeight, setPagesHeight] = useState(0);
  const jumpId = useId();
  const jumps = useMemo(() => pageJumps(doc), [doc]);

  useEffect(() => {
    const frame = frameRef.current;
    const pages = pagesRef.current;
    if (!frame || !pages) return undefined;
    const measure = (): void => {
      setFrameWidth(frame.clientWidth);
      setPagesHeight(pages.offsetHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    observer.observe(pages);
    return () => observer.disconnect();
  }, []);

  const available = (frameWidth > 0 ? frameWidth : FALLBACK_FRAME_WIDTH_PX) - SIDE_GUTTER_PX * 2;
  const scale = Math.min(1, available / PAGE_WIDTH_PX);

  const jumpTo = (key: string): void => {
    pagesRef.current
      ?.querySelector(`[data-page-key="${CSS.escape(key)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-portal-line bg-white px-4 py-2">
        <label htmlFor={jumpId} className="shrink-0 text-portal-tiny font-bold">
          Jump to page
        </label>
        <SelectInput
          id={jumpId}
          density="compact"
          className="h-10 min-w-0 flex-1"
          value=""
          onChange={(event) => jumpTo(event.target.value)}
        >
          <option value="" disabled>
            {jumps.length} pages
          </option>
          {jumps.map((jump) => (
            <option key={jump.key} value={jump.key}>
              {jump.label}
            </option>
          ))}
        </SelectInput>
      </div>
      <div ref={frameRef} className="bg-desk py-4" aria-label="Proposal preview" role="region">
        <div
          className="overflow-hidden"
          style={{ width: PAGE_WIDTH_PX * scale, height: pagesHeight * scale, margin: '0 auto' }}
        >
          <div
            ref={pagesRef}
            style={{ width: PAGE_WIDTH_PX, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          >
            <DocumentView doc={doc} />
          </div>
        </div>
      </div>
    </div>
  );
}
